import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  TextField,
  Button,
  Stack,
  Fab,
  Zoom,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import SendIcon from '@mui/icons-material/Send';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'; // Thêm icon cho nút Scroll to Top

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  // Footer menu links - Đảm bảo tất cả các khóa dịch đều tồn tại
  const footerLinks = {
    company: [
      { name: t('footer.about'), path: '/about' },
      { name: t('footer.team') || 'Our Team', path: '/about#our-team' },
      { name: t('footer.careers') || 'Careers', path: '/careers' },
      { name: t('footer.contact') || 'Contact', path: '/contact' },
    ],
    resources: [
      { name: t('footer.blog') || 'Blog', path: '/blog' },
      { name: t('footer.docs') || 'Documentation' }, //docs
      { name: t('footer.community') || 'Community' }, //community
      { name: t('footer.help') || 'Help Center' }, //help
    ],
    legal: [
      { name: t('footer.terms') || 'Terms of Service' }, //terms-of-service
      { name: t('footer.privacy') || 'Privacy Policy' }, //privacy-policy
      { name: t('footer.cookies') || 'Cookies Policy' }, //cookies
      { name: t('footer.licenses') || 'Licenses' }, //licenses
    ],
  };
  // Thêm state và logic cho nút Scroll to Top
  const [showButton, setShowButton] = useState(false); // Thêm state

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        // Hiển thị nút khi cuộn xuống 300px
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll); // Cleanup
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Cuộn mượt mà lên đầu trang
  };

  return (
    <Box component="footer" sx={{ bgcolor: 'background.paper', pt: 8, pb: 3 }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Logo and info */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              UniLM
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              paragraph
              sx={{ maxWidth: '300px', mb: 3 }}
            >
              {t('footer.description') ||
                'Unisysvn | UniLM provides comprehensive CNC, robotics, and automation solutions for Vietnamese industry.'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton
                aria-label="facebook"
                color="primary"
                size="small"
                // component="a"
                // href="https://www.facebook.com/nguyen.minh.quan.376929/" // Thay link FB của bạn vào đây
                // target="_blank"
                // rel="noopener noreferrer" // Bảo mật khi mở tab mới
                sx={{ '&:hover': { color: '#1877F2' } }}
              >
                <FacebookIcon />
              </IconButton>
              <IconButton
                aria-label="twitter"
                color="primary"
                size="small"
                sx={{ '&:hover': { color: '#1DA1F2' } }}
              >
                <TwitterIcon />
              </IconButton>
              <IconButton
                aria-label="linkedin"
                color="primary"
                size="small"
                sx={{ '&:hover': { color: '#0A66C2' } }}
              >
                <LinkedInIcon />
              </IconButton>
              <IconButton
                aria-label="instagram"
                color="primary"
                size="small"
                sx={{ '&:hover': { color: '#E4405F' } }}
              >
                <InstagramIcon />
              </IconButton>
              <IconButton
                aria-label="youtube"
                color="primary"
                size="small"
                sx={{ '&:hover': { color: '#FF0000' } }}
              >
                <YouTubeIcon />
              </IconButton>
            </Stack>
          </Grid>

          {/* Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              {t('footer.company') || 'Company'}
            </Typography>
            <Box>
              {footerLinks.company.map(link => (
                <Box key={link.path} sx={{ mb: 1 }}>
                  <Link
                    component={RouterLink}
                    to={link.path}
                    color="text.secondary"
                    underline="hover"
                    variant="body2"
                  >
                    {link.name}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              {t('footer.resources') || 'Resources'}
            </Typography>
            <Box>
              {footerLinks.resources.map(link => (
                <Box key={link.path} sx={{ mb: 1 }}>
                  <Link
                    component={RouterLink}
                    to={link.path}
                    color="text.secondary"
                    underline="hover"
                    variant="body2"
                  >
                    {link.name}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              {t('footer.legal') || 'Legal'}
            </Typography>
            <Box>
              {footerLinks.legal.map(link => (
                <Box key={link.path} sx={{ mb: 1 }}>
                  <Link
                    component={RouterLink}
                    to={link.path}
                    color="text.secondary"
                    underline="hover"
                    variant="body2"
                  >
                    {link.name}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Newsletter */}
          <Grid item xs={12} sm={8} md={2}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              {t('footer.newsletter') || 'Newsletter'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('footer.newsletterDesc') || 'Subscribe to our newsletter for the latest updates'}
            </Typography>
            {/* <Box component="form" sx={{ display: 'flex', mb: 1 }}>
              <TextField
                size="small"
                label={t('footer.email') || 'Email'}
                variant="outlined"
                sx={{ flex: 1, mr: 1 }}
              />
              <Button
                variant="contained"
                color="primary"
                aria-label="subscribe"
                size="small"
                sx={{ minWidth: '0', px: 2 }}
              >
                <SendIcon fontSize="small" />
              </Button>
            </Box> */}
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Copyright */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 0 } }}>
            © {currentYear} UniLM. {t('footer.rights') || 'All Rights Reserved'}
          </Typography>
          {/* <Box>
            <Link href="#" color="text.secondary" underline="hover" variant="body2" sx={{ mx: 1 }}>
              {t('footer.terms') || 'Terms of Service'}
            </Link>
            <Link href="#" color="text.secondary" underline="hover" variant="body2" sx={{ mx: 1 }}>
              {t('footer.privacy') || 'Privacy Policy'}
            </Link>
            <Link href="#" color="text.secondary" underline="hover" variant="body2" sx={{ mx: 1 }}>
              {t('footer.cookies') || 'Cookies Policy'}
            </Link>
          </Box> */}
        </Box>
      </Container>
      <Zoom in={showButton}>
        <Fab
          color="primary"
          size="small"
          onClick={handleScrollToTop}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    </Box>
  );
};

export default Footer;
